//import packages
import React, { useState, useCallback } from "react";
import {
  Layout,
  Button,
  Modal,
  Tabs,
  Typography,
  message
} from "antd";

import { PlusCircleOutlined, ExperimentOutlined } from '@ant-design/icons'
import styled from "styled-components";
//import other files and API function
import UserRolesAndRightsHOComponent from "../../../components/UserRolesAndRightsHOComponent/UserRolesAndRightsHOComponent";
import TemplatesTable from "./TemplatesTable";
import TemplateForm from './TemplateForm';
import { PrescriptionTemplates } from "./PrescriptionTemplates"
import { PrescriptionTemplateForm } from "./PrescriptionTemplates/components/PrescriptionTemplateForm";
import { useTemplates } from "./hooks/useTemplates"
import { TelemedicineCategoryProvider } from "../../../context/TelemedicineCategoryContext";
import { CompoundsModal } from "./PrescriptionTemplates/components/CompoundsModal";

import { TemplateTypeEnum } from "../../../constants/enums";
import PharmacyNoteConfigs from "../PharmacyNoteConfigs";

const PHARMACY_NOTE_CONFIGS_KEY = 'pharmacy-note-configs';

const { Content } = Layout;
const { Title } = Typography

const StyledComponentWrapper = styled.div`
  background-color: #ffffff;
  width: 80vw;
  height: calc(100% - 48px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  margin: 24px auto;
  padding: 24px 28px 24px 28px;
  @media (max-width: 1200px) {
    max-width: 720px;
  }
  @media (max-width: 788px), (max-width: 922px) {
    max-width: 580px;
  }
  @media (max-width: 720px) {
    max-width: 320px;
  }
`;

const StyledTabs = styled(Tabs)`
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;

  > .ant-tabs-nav {
    flex: 0 0 auto;
  }

  > .ant-tabs-content-holder {
    flex: 1 1 auto;
    min-height: 0;
  }

  > .ant-tabs-content-holder > .ant-tabs-content {
    height: 100%;
    min-height: 0;
  }

  > .ant-tabs-content-holder > .ant-tabs-content > .ant-tabs-tabpane {
    height: 100%;
    min-height: 0;
    overflow: hidden;
  }
`

const StyledModal = styled(Modal)`
  & .ant-modal-header {
    border-bottom-color: #f1f1f1;
  }
`

const Templates = () => {
  const [templateModalIsOpen, setTemplateModalIsOpen] = useState(false);
  const [templateToDuplicate, setTemplateToDuplicate] = useState(null);
  const [type, setType] = useState('case');
  const [compoundsModalOpen, setCompoundsModalOpen] = useState(false);

  const {
    selectTemplate,
    createTemplate,
    editTemplate,
    bulkEditTemplates,
    deleteTemplate,
    setTemplateFilters,
    searchTemplates,
    telemedicineCategories,
    caseStatuses,
    physicians,
    pharmacyOptions,
    categoryOptions,
    stateOptions,
    selectedTemplate,
    loading,
    loadingByType,
    templatesByType,
    filtersByType,
    templates,
    templateFilters
  } = useTemplates(type)

  const handleFinish = useCallback(() => {
    setTemplateModalIsOpen(false)
    selectTemplate(null)
    setTemplateToDuplicate(null)
  }, [selectTemplate]);

  const handleCreateTemplate = async ({ setLoading, ...values }) => {
    try {
        setLoading(true)
        await createTemplate({ ...values })
        handleFinish()
    } catch (e) {
        message.error(e)
    } finally {
        setLoading(false)
    }
  }

  const handleEditTemplate = async ({ setLoading, ...values }) => {
    try {
        setLoading(true)
        await editTemplate(selectedTemplate._id, values)
        handleFinish()
    } catch (e) {
        console.log(e)
        message.error('Error editing prescription template')
    } finally {
        setLoading(false)
    }
}

  const handleChangeTemplateType = templateType => {
    setType(templateType)
  }

  const handleSubmit = selectedTemplate
    ? handleEditTemplate
    : handleCreateTemplate

  const templateForForm = selectedTemplate || templateToDuplicate
  const isDuplicate = !!templateToDuplicate

  return (
    <TelemedicineCategoryProvider>
      <Content style={{ height: '100%', overflow: 'hidden' }}>
      <StyledComponentWrapper>
        <StyledTabs
          activeKey={type}
          onChange={handleChangeTemplateType}
          tabBarExtraContent={
            type !== PHARMACY_NOTE_CONFIGS_KEY && (
              <div style={{ display: 'flex', gap: '8px' }}>
                {type === TemplateTypeEnum.PRESCRIPTION && (
                  <Button
                    icon={<ExperimentOutlined />}
                    onClick={() => setCompoundsModalOpen(true)}
                  >
                    Manage Compounds
                  </Button>
                )}
                <Button
                  type='primary'
                  icon={<PlusCircleOutlined />}
                  onClick={() => setTemplateModalIsOpen(true)}
                >
                  Add
                </Button>
              </div>
            )
          }
          items={[{
            key: TemplateTypeEnum.CASE,
            label: 'Case Templates',

            children: <><TemplatesTable
                loading={loadingByType[TemplateTypeEnum.CASE]}
                type={TemplateTypeEnum.CASE}
                templates={templatesByType[TemplateTypeEnum.CASE] ?? []}
                setTemplateFilters={setTemplateFilters}
                searchTemplates={searchTemplates}
                templateFilters={filtersByType[TemplateTypeEnum.CASE]}
                caseStatuses={caseStatuses}
                physicians={physicians}
                handleFinish={handleFinish}
              /></>
          }, {
            key: TemplateTypeEnum.MESSAGE,
            label: 'Message Templates',

            children: <><TemplatesTable
                loading={loadingByType[TemplateTypeEnum.MESSAGE]}
                type={TemplateTypeEnum.MESSAGE}
                templates={templatesByType[TemplateTypeEnum.MESSAGE] ?? []}
                setTemplateFilters={setTemplateFilters}
                searchTemplates={searchTemplates}
                templateFilters={filtersByType[TemplateTypeEnum.MESSAGE]}
              /></>
          }, {
            key: TemplateTypeEnum.PRESCRIPTION,
            label: 'Prescription Templates',
            style: { height: '100%', width: '100%'},

            children: <><PrescriptionTemplates
                templates={templates}
                templateFilters={filtersByType[TemplateTypeEnum.PRESCRIPTION]}
                loading={loadingByType[TemplateTypeEnum.PRESCRIPTION]}
                selectTemplate={selectTemplate}
                duplicateTemplate={setTemplateToDuplicate}
                bulkEditTemplates={bulkEditTemplates}
                deleteTemplate={deleteTemplate}
                setTemplateFilters={setTemplateFilters}
                pharmacyOptions={pharmacyOptions}
                categoryOptions={categoryOptions}
                stateOptions={stateOptions}
              /></>
          }, {
            key: TemplateTypeEnum.VISIT,
            label: 'Visit Note Templates',
            style: { height: '100%'},

            children: <><TemplatesTable
                  loading={loadingByType[TemplateTypeEnum.VISIT]}
                  type={TemplateTypeEnum.VISIT}
                  templates={templatesByType[TemplateTypeEnum.VISIT] ?? []}
                  telemedicineCategories={telemedicineCategories}
                  setTemplateFilters={setTemplateFilters}
                  searchTemplates={searchTemplates}
                  handleFinish={handleFinish}
                  templateFilters={filtersByType[TemplateTypeEnum.VISIT]}
              /></>
          }, {
            key: PHARMACY_NOTE_CONFIGS_KEY,
            label: 'Pharmacy Note Templates',
            children: <><PharmacyNoteConfigs /></>
          }]} />
      </StyledComponentWrapper>
      { (templateModalIsOpen || selectedTemplate || templateToDuplicate) && (
          <StyledModal
            title={type === TemplateTypeEnum.PRESCRIPTION && (
              <Title level={4} style={{ color: '#7BAD7E' }}>
                { selectedTemplate ? 'Edit' : isDuplicate ? 'Duplicate' : 'Add New' } Prescription Template
              </Title>
            )}
            maskClosable
            destroyOnClose
            centered
            visible
            closable={false}
            width='50vw'
            footer={null}
            onCancel={handleFinish}
          >
            {
              type === TemplateTypeEnum.PRESCRIPTION
                ? <PrescriptionTemplateForm telemedicineCategories={telemedicineCategories} handleFinish={handleFinish} submit={handleSubmit} selectedTemplate={templateForForm} isDuplicate={isDuplicate} />
                : <TemplateForm mode='Add' type={type} handleFinish={handleFinish} loading={loading}/>
            }
          </StyledModal>
      )}
    </Content>
      <CompoundsModal
        open={compoundsModalOpen}
        onClose={() => setCompoundsModalOpen(false)}
      />
    </TelemedicineCategoryProvider>
  );
};

export default UserRolesAndRightsHOComponent(Templates);
